import { Link, useLocation, useParams } from "react-router";
import { CargarMas, TablaRegistros, useHistorial } from "@/features/auditoria";
import { habilitadas, nombres, useInstituciones } from "@/features/instituciones";
import { AccesosUsuario, AccionesCuenta, DatosUsuario, useFicha } from "@/features/usuarios";
import { ErrorApi } from "@/shared/api";
import { t } from "@/shared/i18n";
import { Aviso, CuerpoTarjeta, EncabezadoPagina, Tarjeta, Vacio } from "@/shared/ui";
import { SegunConsulta, useCorreoDe, useTitulo } from "./comun";
import estilos from "./Paginas.module.css";

const f = t.ficha;

/** Aviso que deja la página anterior (por ejemplo, «Invitación enviada»). */
function avisoDe(estado: unknown): string | null {
  if (estado && typeof estado === "object" && "aviso" in estado && typeof estado.aviso === "string") {
    return estado.aviso;
  }
  return null;
}

export function Ficha() {
  const { usuarioId = "" } = useParams();
  const ubicacion = useLocation();
  const ficha = useFicha(usuarioId);
  const instituciones = useInstituciones();
  useTitulo(ficha.data?.usuario.nombre ?? t.usuarios.titulo);
  const aviso = avisoDe(ubicacion.state as unknown);

  const volver = (
    <p className={estilos.migas}>
      <Link to="/">← {t.general.volver}</Link>
    </p>
  );

  if (ficha.error instanceof ErrorApi && ficha.error.estado === 404) {
    return (
      <div className={estilos.pila}>
        {volver}
        <Tarjeta>
          <Vacio titulo={f.noEncontradoTitulo}>{f.noEncontradoTexto}</Vacio>
        </Tarjeta>
      </div>
    );
  }

  return (
    <div className={estilos.pila}>
      {volver}
      <SegunConsulta consulta={ficha}>
        {() => {
          if (!ficha.data) return null;
          const { usuario } = ficha.data;
          const lista = instituciones.data ?? [];
          return (
            <>
              <EncabezadoPagina titulo={usuario.nombre} subtitulo={usuario.correo} />
              {aviso && (
                <Aviso tono="bien" rol="status">
                  {aviso}
                </Aviso>
              )}
              <div className={estilos.columnas}>
                <Tarjeta titulo={f.datos}>
                  <CuerpoTarjeta>
                    <DatosUsuario usuario={usuario} />
                  </CuerpoTarjeta>
                </Tarjeta>
                <Tarjeta titulo={f.cuenta}>
                  <CuerpoTarjeta>
                    <p className={estilos.ayuda}>{f.cuentaAyuda}</p>
                    <AccionesCuenta usuario={usuario} />
                  </CuerpoTarjeta>
                </Tarjeta>
              </div>
              <Tarjeta titulo={f.accesos}>
                <CuerpoTarjeta>
                  <p className={estilos.ayuda}>{f.accesosAyuda}</p>
                  <AccesosUsuario ficha={ficha.data} habilitadas={habilitadas(lista)} nombres={nombres(lista)} />
                </CuerpoTarjeta>
              </Tarjeta>
              <Historial usuarioId={usuario.usuarioId} />
            </>
          );
        }}
      </SegunConsulta>
    </div>
  );
}

function Historial({ usuarioId }: { readonly usuarioId: string }) {
  const historial = useHistorial(usuarioId);
  const correoDe = useCorreoDe();
  return (
    <Tarjeta titulo={f.historial}>
      <CuerpoTarjeta>
        <p className={estilos.ayuda}>{f.historialAyuda}</p>
      </CuerpoTarjeta>
      <SegunConsulta consulta={historial}>
        {() => (
          <>
            <TablaRegistros
              registros={historial.data?.pages.flatMap((p) => p.registros) ?? []}
              correoDe={correoDe}
              vacio={f.historialVacio}
            />
            <CargarMas
              hayMas={historial.hasNextPage}
              cargando={historial.isFetchingNextPage}
              alCargar={() => void historial.fetchNextPage()}
            />
          </>
        )}
      </SegunConsulta>
    </Tarjeta>
  );
}
